import React from 'react';
import { Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Inline-editable variant of DetailField.
 * Click to switch to input mode, Enter/blur to save, Escape to cancel.
 * Optimistic update is handled by the parent's onSave.
 * Empty string + numeric type → null (clears the field on the backend).
 */
const EditableDetailField = ({
  label,
  field,
  rawValue,
  type = 'text',
  suffix,
  format,
  onSave,
  fallback = 'Not set',
}) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const inputRef = React.useRef(null);

  const isEmpty = rawValue == null || rawValue === '';
  const displayValue = format ? format(rawValue) : (isEmpty ? null : String(rawValue));
  const showEmpty = !displayValue;

  const enterEdit = () => {
    if (saving) return;
    setDraft(rawValue == null ? '' : String(rawValue));
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const cancel = () => {
    setEditing(false);
    setDraft('');
  };

  const commit = async () => {
    if (saving) return;
    const trimmed = String(draft).trim();
    let nextValue;
    if (trimmed === '') {
      nextValue = null;
    } else if (type === 'number') {
      const n = Number(trimmed);
      if (Number.isNaN(n)) {
        toast.error(`${label} must be a number.`);
        return;
      }
      nextValue = n;
    } else if (type === 'email') {
      // Basic shape check; backend is source of truth
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        toast.error('Please enter a valid email address.');
        return;
      }
      nextValue = trimmed;
    } else if (type === 'date') {
      // <input type="date"> always emits YYYY-MM-DD; trust it.
      if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        toast.error(`${label} must be a valid date.`);
        return;
      }
      nextValue = trimmed;
    } else {
      nextValue = trimmed;
    }

    // No-op if unchanged
    const prevNorm = rawValue == null || rawValue === '' ? null : (type === 'number' ? Number(rawValue) : String(rawValue));
    if (nextValue === prevNorm || (nextValue == null && prevNorm == null)) {
      cancel();
      return;
    }

    setSaving(true);
    try {
      await onSave(field, nextValue);
      setEditing(false);
      setDraft('');
    } catch (e) {
      // Toast already raised by parent; stay in edit mode so user can retry
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`group relative rounded-md border px-2 py-1.5 transition-colors ${
        editing
          ? 'border-teal-300 bg-white ring-1 ring-teal-200'
          : showEmpty
          ? 'border-dashed border-slate-200 bg-slate-50/30 hover:border-slate-300 hover:bg-slate-50'
          : 'border-slate-100 bg-slate-50/50 hover:border-slate-300 hover:bg-white'
      } ${editing ? '' : 'cursor-text'}`}
      onClick={editing ? undefined : enterEdit}
      data-testid={`cc-edit-field-${field}`}
      title={editing ? '' : (showEmpty ? `Click to set ${label.toLowerCase()}` : `Click to edit ${label.toLowerCase()}`)}
    >
      <div className="flex items-center gap-1">
        <div className="text-[10px] uppercase tracking-wide text-slate-400 flex-1 truncate">
          {label}
        </div>
        {saving ? (
          <Loader2 className="w-3 h-3 text-teal-500 animate-spin flex-shrink-0" />
        ) : editing ? null : (
          <Pencil className="w-2.5 h-2.5 text-slate-300 group-hover:text-slate-500 flex-shrink-0 transition-colors" />
        )}
      </div>

      {editing ? (
        <div className="flex items-center gap-1 mt-0.5">
          <input
            ref={inputRef}
            type={type === 'email' ? 'email' : type === 'date' ? 'date' : type === 'number' ? 'text' : 'text'}
            inputMode={type === 'number' ? 'decimal' : undefined}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancel();
              }
            }}
            onBlur={commit}
            disabled={saving}
            data-testid={`cc-edit-input-${field}`}
            className="flex-1 min-w-0 bg-transparent text-xs text-slate-800 font-medium outline-none placeholder:text-slate-300 placeholder:italic"
            placeholder={fallback}
          />
          {suffix ? (
            <span className="text-[10px] text-slate-400 flex-shrink-0">{suffix}</span>
          ) : null}
        </div>
      ) : (
        <div
          className={`text-xs truncate ${
            showEmpty ? 'text-slate-400 italic font-normal' : 'text-slate-700 font-medium'
          }`}
          data-testid={`cc-edit-value-${field}`}
        >
          {showEmpty ? fallback : displayValue}
        </div>
      )}
    </div>
  );
};

export default EditableDetailField;
